import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
} from '@mui/material';
import { getDefaultPermissions, getGrantedToExpert, updateGrantedToExpert } from '../../services/access-control.service';
import { BasePermissionType, ConnectionAccessItem, GrantedToItem, PermissionItem, PatchGrantedToItem } from '../../types/access-control.types';

type SchemaItem = PermissionItem | GrantedToItem;

interface Props {
  open: boolean;
  onClose: () => void;
  expertId: number;
  expertName: string;
  /** 'new' — перед відправкою заявки; 'edit' — зміна прав активного з'єднання */
  mode: 'new' | 'edit';
  /** викликається після підтвердження (тільки для mode='new') */
  onConfirm?: (access: ConnectionAccessItem[], comment: string) => void;
  /** викликається після успішного збереження (тільки для mode='edit') */
  onSaved?: () => void;
}

type SelectedMap = Record<string, string[]>;

const selectedToAccessItems = (selected: SelectedMap): ConnectionAccessItem[] =>
  Object.entries(selected)
    .filter(([, perms]) => perms.length > 0)
    .map(([code, permissions]) => ({ code, permissions }));

export const ConnectionPermissionsDialog: React.FC<Props> = ({
  open,
  onClose,
  expertId,
  expertName,
  mode,
  onConfirm,
  onSaved,
}) => {
  const [schema, setSchema] = useState<SchemaItem[] | null>(null);
  const [selected, setSelected] = useState<SelectedMap>({});
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        if (mode === 'edit') {
          const current = await getGrantedToExpert(expertId);
          setSchema(current);
          const initialSelected: SelectedMap = {};
          for (const item of current) {
            initialSelected[item.code] = item.permissions.filter(p => p.isGranted).map(p => p.code);
          }
          setSelected(initialSelected);
        } else {
          const defaultPerms = await getDefaultPermissions();
          setSchema(defaultPerms);
          const initialSelected: SelectedMap = {};
          for (const item of defaultPerms) {
            initialSelected[item.code] = item.permissions.filter(p => p.isDefault).map(p => p.code);
          }
          setSelected(initialSelected);
        }
      } catch {
        setError('Не вдалося завантажити інформацію про доступи.');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [open, expertId, mode]);

  const toggle = (moduleCode: string, permCode: string) => {
    setSelected(prev => {
      const current = prev[moduleCode] ?? [];
      const next = current.includes(permCode)
        ? current.filter(p => p !== permCode)
        : [...current, permCode];
      return { ...prev, [moduleCode]: next };
    });
  };

  const isChecked = (moduleCode: string, permCode: string) =>
    (selected[moduleCode] ?? []).includes(permCode);

  const handleConfirm = async () => {
    if (mode === 'new') {
      onConfirm?.(selectedToAccessItems(selected), comment);
      return;
    }

    setSaving(true);
    try {
      const payload: PatchGrantedToItem[] = (schema as GrantedToItem[]).map(item => ({
        code: item.code,
        permissions: item.permissions.map(p => ({
          code: p.code,
          isGranted: (selected[item.code] ?? []).includes(p.code),
        })),
      }));
      await updateGrantedToExpert(expertId, payload);
      onSaved?.();
      onClose();
    } catch {
      setError('Не вдалося зберегти доступи. Спробуйте ще раз.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {mode === 'new' ? 'Права доступу для експерта' : 'Управління доступами'}
      </DialogTitle>
      <DialogContent>
        {mode === 'new' && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Оберіть, до яких даних матиме доступ <strong>{expertName}</strong> після прийняття
              заявки. Ви зможете змінити це у будь-який час.
            </Typography>
            <TextField
              label="Коментар до заявки"
              placeholder="Необов'язково — розкажіть про свої цілі або побажання"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              multiline
              rows={2}
              fullWidth
              size="small"
              sx={{ mb: 2 }}
              inputProps={{ maxLength: 500 }}
            />
          </>
        )}
        {mode === 'edit' && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Поточні права доступу <strong>{expertName}</strong> до ваших даних.
          </Typography>
        )}

        {loading ? (
          <Box display="flex" justifyContent="center" sx={{ py: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : schema ? (() => {
          const allPerms = [...new Map<string, BasePermissionType>(
            (schema as Array<{ permissions: BasePermissionType[] }>)
              .flatMap(item => item.permissions)
              .map(p => [p.code, p] as [string, BasePermissionType])
          ).values()];
          return (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Модуль</TableCell>
                  {allPerms.map(perm => (
                    <TableCell key={perm.code} align="center" sx={{ fontWeight: 600 }}>
                      {perm.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {schema.map(item => (
                  <TableRow key={item.code}>
                    <TableCell>{item.label}</TableCell>
                    {allPerms.map(perm => {
                      const available = item.permissions.some(p => p.code === perm.code);
                      return (
                        <TableCell key={perm.code} align="center" padding="checkbox">
                          {available ? (
                            <Checkbox
                              checked={isChecked(item.code, perm.code)}
                              onChange={() => toggle(item.code, perm.code)}
                            />
                          ) : null}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          );
        })() : null}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={saving}>
          Скасувати
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={loading || saving || !!error}
        >
          {mode === 'new' ? 'Надіслати заявку' : 'Зберегти'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
